import {
  type QuerySpecialtiesResponse,
  useQuerySpecialties,
} from '@/hooks/query/useQuerySpecialties';
import { useAreFeatureFlagsResolved } from '@heidi/runtime/feature-flags';
import { TemplateCategory } from '@heidi/schema/template-gallery';
import { FilterSettings, TemplateCommunitySortBy } from '@heidi/templates/contracts';
import { useIsCommunityDefaultFiltersEnabled } from '@heidi/templates/flags';
import { countries } from '@heidi/utils/countries';
import { type QueryUserResponse, useUser } from '@heidi/workspace/data';
import { useRouter } from 'next/router';
import { useCallback, useMemo } from 'react';

export interface UrlFiltersState {
  page: number;
  sort: TemplateCommunitySortBy;
  location: string;
  specialty: string;
  category: TemplateCategory | FilterSettings.ALL;
  search: string;
}

const DEFAULTS: UrlFiltersState = {
  page: 1,
  sort: TemplateCommunitySortBy.MOST_POPULAR,
  location: FilterSettings.ALL,
  specialty: FilterSettings.ALL,
  category: FilterSettings.ALL,
  search: '',
};

// Module-scope selectors so the queries don't re-select on every render.
const selectProfileFilterFields = (user: QueryUserResponse) => ({
  country: user.country,
  specialtyId: user.specialty_id,
});
const selectSpecialtiesData = (data: QuerySpecialtiesResponse) => data.specialties;

/**
 * The gallery filter values a bare URL resolves to: the signed-in user's own
 * country and specialty. `/templates` is also a public page — logged-out
 * visitors never load the user query, so they resolve straight to ALL.
 * Profile values that don't exist in the filter vocabulary (legacy free-text
 * countries, retired specialty ids) fall back to ALL rather than silently
 * filtering on an impossible value.
 */
function useProfileFilterDefaults() {
  // The flag hook returns its default (false) until the provider settles, so
  // resolution also waits on `useAreFeatureFlagsResolved` — otherwise a
  // flag-ON cold load would fetch unfiltered on the bootstrap default and
  // refetch when the real value lands. ERROR counts as settled, so a provider
  // outage falls back to the unfiltered gallery rather than blocking it.
  const areFlagsResolved = useAreFeatureFlagsResolved();
  const isDefaultingEnabled = useIsCommunityDefaultFiltersEnabled();
  const { data: profile, isLoading: isUserLoading } = useUser(selectProfileFilterFields);
  const { data: specialtiesData, isLoading: isSpecialtiesLoading } =
    useQuerySpecialties(selectSpecialtiesData);

  const location = useMemo(() => {
    if (!isDefaultingEnabled || !profile?.country) return DEFAULTS.location;
    return countries.some((country) => country.name === profile.country)
      ? profile.country
      : DEFAULTS.location;
  }, [isDefaultingEnabled, profile?.country]);

  // The gallery filters by the specialty's English name — the canonical 'name' field.
  const specialty = useMemo(() => {
    if (!isDefaultingEnabled || !profile?.specialtyId) return DEFAULTS.specialty;
    return (
      specialtiesData?.find((entry) => entry.id === profile.specialtyId)?.name ??
      DEFAULTS.specialty
    );
  }, [isDefaultingEnabled, profile?.specialtyId, specialtiesData]);

  // Holds the gallery fetch until the defaults are known, so the first request
  // runs with them instead of fetching ALL and re-fetching once the profile
  // lands. Logged-out visitors never enter the profile loading states.
  const isResolving =
    !areFlagsResolved ||
    (isDefaultingEnabled &&
      (isUserLoading || (Boolean(profile?.specialtyId) && isSpecialtiesLoading)));

  return { location, specialty, isResolving, specialtiesData };
}

function parseQueryParam(
  param: string | string[] | undefined,
  defaultValue: string
): string {
  if (!param || typeof param !== 'string') {
    return defaultValue;
  }
  return param;
}

// INITIAL/UNDEFINED are sentinels from the pre-URL-state filters (still
// reachable via old bookmarks); both always meant "no filter applied".
const normalizeFilterValue = (value: string): string =>
  value === String(FilterSettings.INITIAL) || value === String(FilterSettings.UNDEFINED)
    ? String(FilterSettings.ALL)
    : value;

function parseFilterParam(
  param: string | string[] | undefined,
  defaultValue: string
): string {
  if (!param || typeof param !== 'string') {
    return defaultValue;
  }
  return normalizeFilterValue(param);
}

function parsePageParam(param: string | string[] | undefined): number {
  if (!param || typeof param !== 'string') {
    return 1;
  }
  const parsed = parseInt(param, 10);
  if (isNaN(parsed) || parsed < 1) {
    return 1;
  }
  return parsed;
}

function parseSortParam(param: string | string[] | undefined): TemplateCommunitySortBy {
  if (!param || typeof param !== 'string') {
    return DEFAULTS.sort;
  }
  if (Object.values(TemplateCommunitySortBy).includes(param as TemplateCommunitySortBy)) {
    return param as TemplateCommunitySortBy;
  }
  return DEFAULTS.sort;
}

function parseCategoryParam(
  param: string | string[] | undefined
): TemplateCategory | FilterSettings.ALL {
  if (!param || typeof param !== 'string') {
    return DEFAULTS.category;
  }
  if (param === String(FilterSettings.ALL)) {
    return FilterSettings.ALL;
  }
  if (Object.values(TemplateCategory).includes(param as TemplateCategory)) {
    return param as TemplateCategory;
  }
  return DEFAULTS.category;
}

export function useUrlFilters() {
  const router = useRouter();
  const profileDefaults = useProfileFilterDefaults();

  const state = useMemo((): UrlFiltersState => {
    return {
      page: parsePageParam(router.query.page),
      sort: parseSortParam(router.query.sort),
      location: parseFilterParam(router.query.location, profileDefaults.location),
      specialty: parseFilterParam(router.query.specialty, profileDefaults.specialty),
      category: parseCategoryParam(router.query.category),
      search: parseQueryParam(router.query.search, DEFAULTS.search),
    };
  }, [router.query, profileDefaults.location, profileDefaults.specialty]);

  const buildQuery = useCallback(
    (updates: Partial<UrlFiltersState>) => {
      const newState = { ...state, ...updates };
      const query: Record<string, string> = {};

      // Only include values the defaults wouldn't resolve to in the URL. An
      // explicit ALL is meaningful (and kept) when the profile default isn't
      // ALL — it's how a user escapes their own country/specialty default.
      if (newState.page !== DEFAULTS.page) {
        query.page = String(newState.page);
      }
      if (newState.sort !== DEFAULTS.sort) {
        query.sort = newState.sort;
      }
      const location = normalizeFilterValue(newState.location);
      if (location !== profileDefaults.location) {
        query.location = location;
      }
      const specialty = normalizeFilterValue(newState.specialty);
      if (specialty !== profileDefaults.specialty) {
        query.specialty = specialty;
      }
      if (newState.category !== DEFAULTS.category) {
        query.category = newState.category;
      }
      if (newState.search !== DEFAULTS.search) {
        query.search = newState.search;
      }

      return query;
    },
    [state, profileDefaults.location, profileDefaults.specialty]
  );

  const setPage = useCallback(
    (page: number) => {
      void router.push({
        pathname: router.pathname,
        query: buildQuery({ page }),
      });
    },
    [router, buildQuery]
  );

  const setSort = useCallback(
    (sort: TemplateCommunitySortBy) => {
      void router.push({
        pathname: router.pathname,
        query: buildQuery({ sort, page: 1 }),
      });
    },
    [router, buildQuery]
  );

  const setLocation = useCallback(
    (location: string) => {
      void router.push({
        pathname: router.pathname,
        query: buildQuery({ location, page: 1 }),
      });
    },
    [router, buildQuery]
  );

  const setSpecialty = useCallback(
    (specialty: string) => {
      void router.push({
        pathname: router.pathname,
        query: buildQuery({ specialty, page: 1 }),
      });
    },
    [router, buildQuery]
  );

  const setCategory = useCallback(
    (category: TemplateCategory | FilterSettings.ALL) => {
      void router.push({
        pathname: router.pathname,
        query: buildQuery({ category, page: 1 }),
      });
    },
    [router, buildQuery]
  );

  const setSearch = useCallback(
    (search: string) => {
      // Use replace for search to avoid polluting history with every keystroke
      void router.replace(
        {
          pathname: router.pathname,
          query: buildQuery({ search, page: 1 }),
        },
        undefined,
        { shallow: true }
      );
    },
    [router, buildQuery]
  );

  const resetFilters = useCallback(() => {
    // Reset means "show everything", not "back to my defaults" — write the
    // escapes explicitly, since a bare URL would just re-apply the defaults.
    const query: Record<string, string> = {};
    if (profileDefaults.location !== DEFAULTS.location) {
      query.location = String(FilterSettings.ALL);
    }
    if (profileDefaults.specialty !== DEFAULTS.specialty) {
      query.specialty = String(FilterSettings.ALL);
    }
    void router.push({
      pathname: router.pathname,
      query,
    });
  }, [router, profileDefaults.location, profileDefaults.specialty]);

  const resetPage = useCallback(() => {
    void router.replace(
      {
        pathname: router.pathname,
        query: buildQuery({ page: 1 }),
      },
      undefined,
      { shallow: true }
    );
  }, [router, buildQuery]);

  return {
    ...state,
    setPage,
    setSort,
    setLocation,
    setSpecialty,
    setCategory,
    setSearch,
    resetFilters,
    resetPage,
    isReady: router.isReady && !profileDefaults.isResolving,
    specialties: profileDefaults.specialtiesData,
  };
}
